import React, { useEffect, useState } from "react";
import { SortDirection, DataStore } from 'aws-amplify';
import { DiagnoseType, DiagnosisSummary, DayDiagnosis } from '../../models';
import { StyleSheet, Text, ScrollView, TouchableOpacity, View, SafeAreaView, useWindowDimensions } from 'react-native';
import SwitchSelector from "react-native-switch-selector";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Brush, LabelList } from 'recharts';
import { getStorage } from "../components/SessionStorage";
import { Checkbox } from "react-native-paper";
import { Header } from "../components/Header";
import { toLocalString, getDiagnoseTypeUnit } from "../components/CmnUtil";

interface ExistDataKey {
  [key: string]: string[];
};

interface CompareCategory {
  [compareCategory: string]: number | undefined;
};

interface CompareCategoryPerYear {
  [key: string]: CompareCategory | string;
};

// グラフの色
const colors = ["orange", "red", "blue", "green", "purple"];

/** ================================
*  過去データ比較画面 
*  ================================*/
const DiagnosisCompare = ({ navigation }: { navigation: any; }) => {

  // 日別AI診断結果データ取得
  const queryDayDiagnosis = async ({ predicates, sort, page, limit }: { predicates?: any, sort?: any, page?: any, limit?: any; }): Promise<DayDiagnosis[]> => {
    return DataStore.query(DayDiagnosis, predicates, {
      sort,
      page: page ?? 0,
      limit: limit ?? 1000,
    });
  };

  // 日次集計用AI診断結果データ取得
  const queryDiagnosisSummary = async ({ predicates, sort, page, limit }: { predicates?: any, sort?: any, page?: any, limit?: any; }): Promise<DiagnosisSummary[]> => {
    return DataStore.query(DiagnosisSummary, predicates, {
      sort,
      page: page ?? 0,
      limit: limit ?? 1000,
    });
  };

  // windowの幅
  const windowSize = useWindowDimensions();

  // 比較対象を格納
  const [compareCategory, setCompareCategory] = useState<string>(DiagnoseType.VEGETATION_COVERAGE);

  // 診断結果データを格納
  const [diagnosisData, setDiagnosisData] = useState<CompareCategoryPerYear[]>([]);

  // 各年度の各項目のデータが存在しているかどうか
  const [existData, setExistData] = useState<ExistDataKey>({
    vegetationCoverage: ["", "", "", "", ""],
    branchLength: ["", "", "", "", ""],
    bearingNumber: ["", "", "", "", ""],
    fruitSize: ["", "", "", "", ""],
    fruitMaxSize: ["", "", "", "", ""],
    fruitMinSize: ["", "", "", "", ""],
  });

  // 今年度格納
  const [thisFiscalYear, setThisFiscalYear] = useState<number>(0);

  // 最大最小表示切り替え
  const [showMaxMin, setShowMaxMin] = useState<boolean>(false);

  // 診断結果データ格納処理
  useEffect(() => {
    (async () => {
      let addData: CompareCategoryPerYear[] = new Array(366);

      // storageに格納されている圃場のIDを取得
      const res = await getStorage("farm");
      const farmId = typeof res === "undefined" ? "" : res.id;

      // 今年度を取得
      const today = new Date();
      const thisFiscalYear = today.getMonth() > 1 ? today.getFullYear() : today.getFullYear() - 1;
      setThisFiscalYear(thisFiscalYear);

      // 4年度前の3月1日以降の日別AI診断結果データを取得
      const limitDate = (thisFiscalYear - 4) + "-03-01";
      const dayDiagnosises: DayDiagnosis[] = (await queryDayDiagnosis({
        predicates: (c: { date: (arg0: string, arg1: string) => any; }) => c.date("gt", (thisFiscalYear - 4) + "-02-28"),
        sort: (c: { date: (arg0: SortDirection) => any; }) => c.date(SortDirection.ASCENDING),
      })).filter(c => c.farm.id == farmId && c.isAggregated && limitDate <= toLocalString(c.date));

      //4年度前の3月1日以降の日別AI診断結果のIDを取得
      const dayDiagnosisIds = dayDiagnosises.map(c => c.id);

      // 4年度前の3月1日以降の日次集計用AI診断結果データを取得
      const diagnosisSummary: DiagnosisSummary[] = (await queryDiagnosisSummary({ limit: 100000 })).filter(c => dayDiagnosisIds.includes(c.dayDiagnosis.id));

      // 4年度前の3月1日以降の診断結果を日付も併せて取得
      const daignosisSummaryWithDate = dayDiagnosises.map(c => {
        let returnItem: {
          date: string,
          vegetationCoverage: number | undefined,
          branchLength: number | undefined,
          bearingNumber: number | undefined,
          fruitSize: number | undefined,
          fruitMaxSize: number | undefined,
          fruitMinSize: number | undefined,
        } = {
          date: toLocalString(c.date).slice(0, 10),
          vegetationCoverage: undefined,
          branchLength: undefined,
          bearingNumber: undefined,
          fruitSize: undefined,
          fruitMaxSize: undefined,
          fruitMinSize: undefined,
        };

        // 各診断結果の取得
        const summarys = diagnosisSummary.filter(d => d.dayDiagnosis.id == c.id);
        const noneIfZero = (v: number) => v || undefined

        // 植被率
        const vegetationCoverageSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.VEGETATION_COVERAGE);
        if (vegetationCoverageSummary.length != 0) {
          returnItem.vegetationCoverage = vegetationCoverageSummary[0].diagnoseResult;
        };

        // 枝の長さ
        const branchLengthSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.BRANCH_LENGTH);
        if (branchLengthSummary.length != 0) {
          returnItem.branchLength = branchLengthSummary[0].diagnoseResult;
        };

        // 着果数
        const bearingNumberSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.BEARING_NUMBER);
        if (bearingNumberSummary.length != 0) {
          returnItem.bearingNumber = bearingNumberSummary[0].diagnoseResult;
        };

        // サイズ
        const fruitSizeSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.FRUIT_SIZE);
        if (fruitSizeSummary.length != 0) {
          returnItem.fruitSize = noneIfZero(fruitSizeSummary[0].diagnoseResult);
        };

        // Maxサイズ
        const fruitMaxSizeSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.FRUIT_MAX_SIZE);
        if (fruitMaxSizeSummary.length != 0) {
          returnItem.fruitMaxSize = noneIfZero(fruitMaxSizeSummary[0].diagnoseResult);
        };
        const fruitMinSizeSummary = summarys.filter(d => d.diagnoseType == DiagnoseType.FRUIT_MIN_SIZE);
        if (fruitMinSizeSummary.length != 0) {
          returnItem.fruitMinSize = noneIfZero(fruitMinSizeSummary[0].diagnoseResult);
        };

        return returnItem;
      });

      // 日付を格納
      let month = 3;
      let day = 1;
      const daysOfMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      for (let i = 0; i < addData.length; i++) {
        let dayDiagnosis: CompareCategoryPerYear = {
          date: "",
          zero: {
            vegetationCoverage: undefined,
            branchLength: undefined,
            bearingNumber: undefined,
            fruitSize: undefined,
            fruitMaxSize: undefined,
            fruitMinSize: undefined,
          },
          one: {
            vegetationCoverage: undefined,
            branchLength: undefined,
            bearingNumber: undefined,
            fruitSize: undefined,
            fruitMaxSize: undefined,
            fruitMinSize: undefined,
          },
          two: {
            vegetationCoverage: undefined,
            branchLength: undefined,
            bearingNumber: undefined,
            fruitSize: undefined,
            fruitMaxSize: undefined,
            fruitMinSize: undefined,
          },
          three: {
            vegetationCoverage: undefined,
            branchLength: undefined,
            bearingNumber: undefined,
            fruitSize: undefined,
            fruitMaxSize: undefined,
            fruitMinSize: undefined,
          },
          four: {
            vegetationCoverage: undefined,
            branchLength: undefined,
            bearingNumber: undefined,
            fruitSize: undefined,
            fruitMaxSize: undefined,
            fruitMinSize: undefined,
          },
        };
        // 日付を整える
        if (day > daysOfMonth[month - 1]) {
          day = 1;
          month += 1;
        };
        if (month > 12) { month = 1; };
        dayDiagnosis.date = month + "/" + day;

        day++;
        addData[i] = dayDiagnosis;
      };

      // データ存在チェック
      let exist: ExistDataKey = {
        vegetationCoverage: ["", "", "", "", ""],
        branchLength: ["", "", "", "", ""],
        bearingNumber: ["", "", "", "", ""],
        fruitSize: ["", "", "", "", ""],
        fruitMaxSize: ["", "", "", "", ""],
        fruitMinSize: ["", "", "", "", ""],
      };

      // addDataにデータを格納
      daignosisSummaryWithDate.forEach(item => {
        const dayCount = changeToDayCount(item.date.slice(-5));
        let past1 = "zero";
        let past2 = 0;
        if (item.date >= (thisFiscalYear + "-03-01")) {
          past1 = "zero";
          past2 = 0;
        } else if (item.date < (thisFiscalYear + "-03-01") && item.date >= ((thisFiscalYear - 1) + "-03-01")) {
          past1 = "one";
          past2 = 1;
        } else if (item.date < ((thisFiscalYear - 1) + "-03-01") && item.date >= ((thisFiscalYear - 2) + "-03-01")) {
          past1 = "two";
          past2 = 2;
        } else if (item.date < ((thisFiscalYear - 2) + "-03-01") && item.date >= ((thisFiscalYear - 3) + "-03-01")) {
          past1 = "three";
          past2 = 3;
        } else if (item.date < ((thisFiscalYear - 3) + "-03-01") && item.date >= ((thisFiscalYear - 4) + "-03-01")) {
          past1 = "four";
          past2 = 4;
        };
        addData[dayCount][past1] = {
          vegetationCoverage: item.vegetationCoverage == undefined ? undefined : item.vegetationCoverage * 100,
          branchLength: item.branchLength,
          bearingNumber: item.bearingNumber,
          fruitSize: item.fruitSize,
          fruitMaxSize: item.fruitMaxSize,
          fruitMinSize: item.fruitMinSize,
        };

        // データがあれば表示させる
        if (typeof item.vegetationCoverage != undefined) { exist.vegetationCoverage[past2] = past1; };
        if (typeof item.branchLength != undefined) { exist.branchLength[past2] = past1; };
        if (typeof item.bearingNumber != undefined) { exist.bearingNumber[past2] = past1; };
        if (typeof item.fruitSize != undefined) { exist.fruitSize[past2] = past1; };
        if (typeof item.fruitMaxSize != undefined) { exist.fruitMaxSize[past2] = past1; };
        if (typeof item.fruitMinSize != undefined) { exist.fruitMinSize[past2] = past1; };
      });

      setExistData(exist);
      setDiagnosisData(addData);
    })();
  }, []);

  const CustomizedDot = (props: any) => {
    const { cx, cy, stroke, payload, value, color, type } = props;
    return (
      <svg x={cx - 5} y={cy - 5} width={20} height={20} fill={color} viewBox="0 0 1024 1024" style={{ opacity: 0.5 }}>
        {value != undefined && (
          type == "Max" ?
            <rect height="500" width="500" fill={color} />
            :
            <polygon x="5000" points="0,500 250,0 500,500" fill={color} />
        )}
      </svg>
    );
  };

  const CustomizedDateLabel = ({ x, y, stroke, value }: any) => {
    let content = (<></>);
    if (y != null) {
      content = (
        <text x={x} y={y} dy={y < 25 ? 0 : -25} fill={stroke} fontSize={12} textAnchor="middle">
          {value}
        </text>
      );
    }
    return content;
  };

  const CustomizedValueLabel = ({ x, y, stroke, value }: any) => {
    let content = (<></>);
    if (y != null) {
      content = (
        <text x={x} y={y} dy={y < 25 ? 15 : -10} fill={stroke} fontSize={12} textAnchor="middle">
          {value.toFixed(2)}
        </text>
      );
    }
    return content;
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={{ width: "100%", flex: 1 }}>
        <Header onPress={() => navigation.goBack()} goBackText="＜ 診断結果一覧へ" />
        <View style={{ width: "100%", flex: 1, justifyContent: "center", alignItems: "center", }}>
          <View style={styles.wrapper}>
            <View style={{ marginBottom: 35, }}>
              <Text style={styles.title}>過去データと比較</Text>
            </View>
            <View style={{ marginBottom: 40, }}>
              <SwitchSelector
                initial={0}
                onPress={(value: string) => setCompareCategory(value)}
                textColor="#ffffff"
                selectedColor="#ffffff"
                backgroundColor="#808080"
                buttonColor="#7AAD47"
                borderColor="#057E32"
                fontSize={15}
                borderRadius={0}
                hasPadding
                height={50}
                options={[
                  {
                    label: "植被率",
                    value: DiagnoseType.VEGETATION_COVERAGE
                  },
                  {
                    label: "枝長",
                    value: DiagnoseType.BRANCH_LENGTH
                  },
                  {
                    label: "着果数",
                    value: DiagnoseType.BEARING_NUMBER
                  },
                  {
                    label: "果実\nサイズ",
                    value: DiagnoseType.FRUIT_SIZE
                  },
                ]}
              />
            </View>

            {/* グラフ */}
            <View style={{ height: 400 }}>
              <ResponsiveContainer height={"100%"} width={"100%"}>
                <LineChart data={diagnosisData} style={{ left: -25 }} >
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} ticks={["3/1", "6/1", "9/1", "12/1"]} />
                  <YAxis axisLine={false} tickLine={false} unit={getDiagnoseTypeUnit(compareCategory)} />
                  {
                    existData[compareCategory].map((num, key) => {
                      return num == "" ?
                        <View key={key}></View>
                        :
                        <Line key={key} type="linear" connectNulls dataKey={num + "." + compareCategory} stroke={colors[(thisFiscalYear - key) % colors.length]} fill={colors[(thisFiscalYear - key) % colors.length]} strokeWidth={3} isAnimationActive={false} >
                          <LabelList dataKey={"date"} content={<CustomizedDateLabel />} />
                          <LabelList dataKey={num + "." + compareCategory} content={<CustomizedValueLabel />} />
                        </Line>;
                    })
                  }
                  {(compareCategory == DiagnoseType.FRUIT_SIZE && showMaxMin) && (
                    existData[compareCategory].map((num, key) => {
                      return num == "" ?
                        <View key={key}></View>
                        :
                        [
                          <Line type="linear" connectNulls dataKey={num + "." + DiagnoseType.FRUIT_MAX_SIZE} stroke={colors[(thisFiscalYear - key) % colors.length]} fill={colors[(thisFiscalYear - key) % colors.length]} strokeWidth={1} isAnimationActive={false} strokeDasharray="5 5" dot={<CustomizedDot type="Max" color={colors[(thisFiscalYear - key) % colors.length]} />} opacity={0.5}>
                            <LabelList dataKey={"date"} content={<CustomizedDateLabel />} />
                            <LabelList dataKey={num + "." + DiagnoseType.FRUIT_MAX_SIZE} content={<CustomizedValueLabel />} />
                          </Line>,
                          <Line type="linear" connectNulls dataKey={num + "." + DiagnoseType.FRUIT_MIN_SIZE} stroke={colors[(thisFiscalYear - key) % colors.length]} fill={colors[(thisFiscalYear - key) % colors.length]} strokeWidth={1} isAnimationActive={false} strokeDasharray="5 5" dot={<CustomizedDot type="Min" color={colors[(thisFiscalYear - key) % colors.length]} />} opacity={0.5}>
                            <LabelList dataKey={"date"} content={<CustomizedDateLabel />} />
                            <LabelList dataKey={num + "." + DiagnoseType.FRUIT_MIN_SIZE} content={<CustomizedValueLabel />} />
                          </Line>,
                        ];
                    })
                  )}
                  <Brush
                    className="TimeLineChart-brush"
                    dataKey="key"
                    stroke="#8884d8"
                    travellerWidth={10}
                    startIndex={0}
                    endIndex={180}
                  />
                </LineChart>
              </ResponsiveContainer>
            </View>
            {
              existData[compareCategory].map((num, key) => {
                return num == "" ?
                  <View key={key}></View>
                  :
                  compareCategory == DiagnoseType.FRUIT_SIZE && showMaxMin ?
                    <View key={key} style={{ flexDirection: "row", marginHorizontal: "auto", }}>
                      <View style={{ flexDirection: "row", marginHorizontal: 10, alignItems: "center", }}>
                        <svg width={14} height={14} fill={colors[(thisFiscalYear - key) % colors.length]} viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="50" stroke={colors[(thisFiscalYear - key) % colors.length]} fill={colors[(thisFiscalYear - key) % colors.length]} />
                        </svg>
                        <Text style={{ fontSize: 16, marginLeft: 5, lineHeight: 30, }}>{thisFiscalYear - key}平均</Text>
                      </View>
                      <View style={{ flexDirection: "row", marginHorizontal: 10, alignItems: "center", }}>
                        <svg width={14} height={14} fill={colors[(thisFiscalYear - key) % colors.length]} viewBox="0 0 100 100">
                          <rect height="100" width="100" fill={colors[(thisFiscalYear - key) % colors.length]} />
                        </svg>
                        <Text style={{ fontSize: 16, marginLeft: 5, lineHeight: 30, }}>{thisFiscalYear - key}最大</Text>
                      </View>
                      <View style={{ flexDirection: "row", marginHorizontal: 10, alignItems: "center", }}>
                        <svg width={14} height={14} fill={colors[(thisFiscalYear - key) % colors.length]} viewBox="0 0 100 100">
                          <polygon points="50,0 0,100 100,100" fill={colors[(thisFiscalYear - key) % colors.length]} />
                        </svg>
                        <Text style={{ fontSize: 16, marginLeft: 5, lineHeight: 30, }}>{thisFiscalYear - key}最小</Text>
                      </View>
                    </View>
                    :
                    <View key={key} style={{ flexDirection: "row", marginHorizontal: "auto", alignItems: "center", }}>
                      <svg width={14} height={14} fill={colors[(thisFiscalYear - key) % colors.length]} viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="50" stroke={colors[(thisFiscalYear - key) % colors.length]} fill={colors[(thisFiscalYear - key) % colors.length]} />
                      </svg>
                      <Text style={{ fontSize: 16, marginLeft: 5, lineHeight: 30, }}>{thisFiscalYear - key}平均</Text>
                    </View>;
              })
            }

            {compareCategory == DiagnoseType.FRUIT_SIZE && <View style={{ flexDirection: "row", justifyContent: "center", alignItems: "center", }}>
              <Checkbox
                status={showMaxMin ? "checked" : "unchecked"}
                onPress={() => {
                  setShowMaxMin(!showMaxMin);
                }}
                color="#7AAD47"
              />
              <TouchableOpacity onPress={() => { setShowMaxMin(!showMaxMin); }}>
                <Text style={{ fontSize: 22, }} >最大値/最小値を表示する</Text>
              </TouchableOpacity>
            </View>}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: "#E2F0D9",
  },
  wrapper: {
    width: "90%",
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
  },
  legendStyle: {
    fontSize: 16,
    color: "#000000",
  },
});

// 補助関数
/**
 * 3月1日からの経過日数を取得する（3月1日は0）
 * @param monthDate {string} mm/ddの形式で指定
 * @returns {number} 3月1日からの経過日数
 */
const changeToDayCount = (monthDate: string): number => {
  const month = Number(monthDate.slice(0, 2));
  const date = Number(monthDate.slice(-2));
  let count = date - 1;
  switch (month) {
    case 2:
      count += 31;
    case 1:
      count += 31;
    case 12:
      count += 30;
    case 11:
      count += 31;
    case 10:
      count += 30;
    case 9:
      count += 31;
    case 8:
      count += 31;
    case 7:
      count += 30;
    case 6:
      count += 31;
    case 5:
      count += 30;
    case 4:
      count += 31;
    case 3:
      count += 0;
  };
  return count;
};

export default DiagnosisCompare;
